/**
 * Skeleton primitives — Sprint BQ.
 *
 * The existing `LoadingBlock` (a spinner + label) reads fine on a
 * fast network but feels jumpy on the slow LTE most operators
 * have in the field. These shapes give every page a stand-in
 * geometry so the first paint resembles the eventual layout —
 * the eye fixes on the right position before data arrives.
 *
 * Three patterns, deliberately small:
 *
 *   - `<SkeletonBar>` — a single shimmering rectangle. Building
 *     block for the other two.
 *   - `<SkeletonTable rows cols>` — table-shaped placeholder for
 *     list pages (Listings / Customers / AuditLog / etc.).
 *   - `<SkeletonTiles count>` — card-shaped placeholder for the
 *     stat-tile rows on Usage / Platform / CostDashboard.
 *
 * Animation is a one-line CSS shimmer; no library. The animation
 * class is `animate-pulse` from Tailwind plus a tinted bg so
 * dark-mode shimmers stay legible.
 */

import { useT } from "../i18n";


export function SkeletonBar(props: {
  className?: string;
}): JSX.Element {
  return (
    <div
      className={
        "h-3 animate-pulse rounded-sm bg-base-200/70 dark:bg-base-700/40 " +
        (props.className ?? "")
      }
      aria-hidden="true"
    />
  );
}


export function SkeletonTable(props: {
  rows?: number;
  cols?: number;
}): JSX.Element {
  const t = useT();
  const rows = props.rows ?? 8;
  const cols = props.cols ?? 5;
  return (
    <div className="space-y-2" role="status" aria-busy="true" aria-label={t("skeleton.aria.table")}>
      {/* Header row — slightly darker so the eye reads it as a heading. */}
      <div className="flex items-center gap-3 border-b border-base-200 pb-2">
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonBar key={i} className={
            i === 0 ? "w-24" : i === cols - 1 ? "w-16" : "flex-1"
          } />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-3 py-1.5">
          {Array.from({ length: cols }).map((_, c) => (
            <SkeletonBar key={c} className={
              c === 0 ? "h-2.5 w-24"
                : c === cols - 1 ? "h-2.5 w-16"
                : "h-2.5 flex-1"
            } />
          ))}
        </div>
      ))}
    </div>
  );
}


export function SkeletonTiles(props: {
  count?: number;
}): JSX.Element {
  const t = useT();
  const n = props.count ?? 4;
  return (
    <div
      className="grid grid-cols-12 gap-3"
      role="status" aria-busy="true" aria-label={t("skeleton.aria.stats")}
    >
      {Array.from({ length: n }).map((_, i) => (
        <div
          key={i}
          className="col-span-3 rounded-sm border border-base-200 bg-surface px-3 py-2"
        >
          <SkeletonBar className="h-2 w-16" />
          <SkeletonBar className="mt-2 h-5 w-24" />
          <SkeletonBar className="mt-2 h-2 w-12" />
        </div>
      ))}
    </div>
  );
}
